//Imports
import React, { useCallback, useEffect, useRef, useState } from "react";

//Controls
import { Button, Modal, Form, Tooltip, IconButton, Icons } from "@gpstrackit/gtc-react-controls";
import { Icon } from "@mui/material";
import Tabs, { TabContainer } from 'Components/Tabs';

// Helpers
import {
    getValueFloat,
    GetFormatForMoment,
    getFormattedDateByTimeZone,
    convertToKPHWithDecimals,
    convertToMI,
    vinValidation
} from "Core/data/Helpers";

//Components
import {
    NotificationManager,
    NotificationContainer,
} from "Components/Notifications";

//Hooks
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useRealtimeUnits, useRealtimeDrivers } from 'Core/hooks';
import { HasPermission } from "Core/security"

import { upsertManyWithRules as upsertRealtimeUnits } from 'Core/realtime/redux/unitSlice';
import { upsertManyWithRules as upsertRealtimeLastReadings } from 'Core/realtime/redux/lastReadingSlice';
import { upsertManyWithRules as upsertRealtimeAssets } from 'Core/realtime/redux/assetSlice';

//Actions
import {
    fleetModalSearchEntityLists,
    fleetModalSearchAssetSubtypes,
    fleetModalUpdateUnit,
    fleetModalGetUnit,
    reset
} from "./redux/slice";
import { catalogLoad, openTroubleshootDeviceModal, catalogProcessItem, catalogProcessItems, clearRedux } from 'Redux/actions'

//Views
import Unit from './views/unit';
import Asset from './views/asset';
import InputOutput from './views/inputOutputs';
import Notes from './views/notes';
import Tags from './views/tags';
import { hasInvalidUnitOutputs } from './views/inputOutputs';

//Styles
import './index.scss'

const ModalUpdateUnit = ({ refresh }) => {

    //Redux
    const dispatch = useDispatch();
    const { open, loading, result, error, unit, unitId, loadingDecodeVin, deviceVin, loadingAssetSubtypes, entityLists, fleetModalDecodeVin, loadingAssetTypeConfig } = useSelector((state) => state.fleetModalUpdateUnitRedux);


    const unitFormRef = Form.useForm();

    //Language
    const { messages, locale } = useIntl();

    //Redux States
    const inputLabels = useSelector(state => state.catalogsRedux.inputLabels);
    const vehicleStatus = useSelector(state => state.catalogsRedux.vehicleStatus);
    const user = useSelector((state) => state.securityRedux.user);
    const contacts = useSelector(state => state.catalogsRedux.contacts);
    const units = useRealtimeUnits({ trackFields: ['id', 'name', 'label', 'disabled', 'driverId', 'vehicleStatusId', 'tags'] });
    const drivers = useRealtimeDrivers({ trackFields: ['id', 'name', 'externalId', 'disabled', 'tags'] });

    //Local States
    const [udpateVehicleStatusIcon, setUpdateVehicleStatusIcon] = useState(false);
    const [supportedInputs, setSupportedInputs] = useState(0);
    const [supportedOutputs, setSupportedOutputs] = useState(0);
    const [outputsCounter, setOutputsCounter] = useState([]);
    const [inputsCounter, setInputsCounter] = useState([]);
    const [enableAll, setEnableAll] = useState(true);
    const [formData, setFormData] = useState({});
    const [changes, setChanges] = useState({});
    const [tab, setTab] = useState(0);
    const [formWithErrors, setFormWithErrors] = useState(false);
    const [isEllipsis, setIsEllipsis] = useState(false);
    const titleRef = useRef();

    const formatMiles = (value) => {
        const num = Number(value);
        if (!Number.isFinite(num)) return 0;
        return Number(num.toFixed(2));
    };

    // The whole form addresses outputs by position, so index must always be position - 1.
    const buildPositionalOutputs = (currentUnit) => {
        const supported = Number(currentUnit?.unitVersion?.supportedOutputs) || 0;
        const positionalOutputs = new Array(supported).fill(null);

        (currentUnit?.outputs || []).forEach((output) => {
            const index = Number(output?.position) - 1;
            if (index >= 0) positionalOutputs[index] = output;
        });

        return positionalOutputs;
    };
    
    //Handlers and utils
    const handleClose = () => {
        setUpdateVehicleStatusIcon(false)
        setSupportedInputs(0)
        setSupportedOutputs(0)
        setOutputsCounter([])
        setInputsCounter([])
        dispatch(reset());
        setFormWithErrors(false);
        setEnableAll(true);
        setChanges({});
        setFormData({})
    }

    const serializeMaintenanceCompany = (value) => {
        if (!value) return "";
        if (typeof value === "string") return value; // already in backend shape
        if (!value.id) return "";
        return `{id=${value.id}, name=${value.name ?? value.id}}`;
    };

    const handleUpdateSubmit = useCallback(() => {
        const unitInputs = { c: [], u: [], d: [] };
        formData.inputs.map((item, index) => {
            if (item) {
                const _unit = unit?.inputs.find((input) => input.labelId === item.labelId && input.position === item.position)

                if (_unit !== undefined) {
                    unitInputs.u.push({
                        unitId: formData.id,
                        position: item.position,
                        labelId: item.id || item.labelId,
                    })
                } else {
                    unitInputs.c.push({
                        unitId: formData.id,
                        position: item.position,
                        labelId: item.id,
                    })
                }
            }
        });

        const inputsToDelete = unit?.inputs.filter(input => !formData.inputs.some(item => input.labelId === item.labelId && item.position === input.position));
        if (inputsToDelete.length > 0) {
            inputsToDelete.map(input => {
                unitInputs.d.push({ unitId: formData.id, position: input.position });
            });
        }

        const unitOutputs = { c: [], u: [], d: [] };

        //** Create outputs individually, empty rows are skipped without shifting the rest */
        formData.outputs.forEach((item, index) => {
            const position = index + 1;
            const alreadySaved = unit?.outputs?.some((current) => current.position === position);

            if (!item?.name || !item?.type) {
                if (alreadySaved) unitOutputs.d.push({ unitId: formData.id, position });
                return;
            }

            const output = {
                unitId: formData.id,
                position,
                name: item.name,
                type: item.type?.type || item.type || 'Toggle',
                state: item.state || "Off",
            };

            if (alreadySaved) {
                unitOutputs.u.push(output);
            } else {
                unitOutputs.c.push({ ...output, state: "Off" });
            }
        });

        let tagIds = [];
        tagIds = formData.tags.map((tag) => {
            return Number(tag.id);
        });

        let objectTosave = { ...formData, tagIds };
        let alertContacts = formData?.alertContacts?.map(alertContact => alertContact?.id) || [];

        if (changes?.vin && formData?.vin && !vinValidation(formData.vin)) {
            NotificationManager.warning(
                messages['fleetModalUpdateUnit_errorVin'],
                messages["warning"],
                3000
            );
            return;
        }

        if (changes?.licensePlate && formData?.licensePlate && formData?.licensePlate?.length < 4) {
            NotificationManager.error(
                messages['fleetModalUpdateUnit_licensePlateErrorLength'],
                messages["error"],
                3000
            );
            return;
        }

        objectTosave.offsetEngineOdometer = Number(
            user.isKph
                ? convertToMI(formData.offsetEngineOdometer)
                : formData.offsetEngineOdometer
        );

        objectTosave.offsetEcuEngineOdometer = Number(
            user.isKph
                ? convertToMI(formData.offsetEcuEngineOdometer)
                : formData.offsetEcuEngineOdometer
        );

        objectTosave = {
            ...objectTosave,
            obd2alerts: unit?.unitVersion?.obd2alerts,
            vbus: unit?.vbus,
            jbus: unit?.jbus,
            alertContacts: alertContacts,
            asset: {
                label: formData?.label || "",
                ...(changes.assetType && { assetTypeId: formData?.assetType?.entityId ?? formData?.assetTypeId ?? "" }),
                ...(changes.assetSubtype && { assetSubtypeId: formData?.assetSubtype?.id ?? formData?.assetSubtypeId ?? "" }),
                ...(changes.vin && { vin: formData?.vin }),
                ...(changes.make && { make: formData?.make || "" }),
                ...(changes.model && { model: formData?.model || "" }),
                ...(changes.year && { year: typeof formData?.year === "number" ? formData?.year : Number(formData?.year) || 0 }),
                ...(changes.engine && { engine: formData?.engine ? parseFloat(formData?.engine) : 0 }),
                ...(changes.mpgHighway && { mpgHighway: typeof formData?.mpgHighway === "number" ? formData.mpgHighway : Number(formData.mpgHighway) || 0 }),
                ...(changes.mpgCity && { mpgCity: typeof formData?.mpgCity === "number" ? formData.mpgCity : Number(formData.mpgCity) || 0 }),
                ...(changes.licensePlate && { licensePlate: formData.licensePlate, assetTypeId: formData?.assetType?.entityId ?? formData?.assetTypeId ?? "" }),
                ...(changes.registrationState && {
                    registrationState:
                        (formData?.registrationState?.entityId ?? formData?.registrationState) === "NONE"
                            ? ""
                            : (formData?.registrationState?.entityId ?? formData?.registrationState ?? "")
                }),
                ...(changes.registrationCountry && {
                    registrationCountry:
                        (formData?.registrationCountry?.entityId ?? formData?.registrationCountry) === "NONE"
                            ? ""
                            : (formData?.registrationCountry?.entityId ?? formData?.registrationCountry ?? "")
                }),
                ...(changes.registrationExpiration && {
                    registrationExpiration: formData?.registrationExpiration
                        ? new Date(formData.registrationExpiration).toISOString().slice(0, 10)
                        : ""
                }),
                ...(changes.inspectionDeviceType && {
                    inspectionDeviceType: formData?.inspectionDeviceType?.entityId ?? formData?.inspectionDeviceType ?? ""
                }),
                ...(changes.inspectionTagId && { inspectionTagId: formData?.inspectionTagId }),
                ...(changes.powerUnitNumber && { powerUnitNumber: formData?.powerUnitNumber || '' }),
                ...(changes.externalId && { externalId: formData?.externalId }),
                ...(changes.isActive && { isActive: typeof formData?.isActive === "object" ? !!formData?.isActive?.id : !!formData?.isActive }),
                ...(changes.chassisSerialNumber && { chassisSerialNumber: formData?.chassisSerialNumber || '' }),
                ...(changes.engineSerialNumber && { engineSerialNumber: formData?.engineSerialNumber || '' }),
                ...(changes.maintenanceCompany && {
                    maintenanceCompany: serializeMaintenanceCompany(formData?.maintenanceCompany)
                })
            }

        };

        if (!objectTosave.vehicleStatusId) {
            objectTosave.vehicleStatusId = 0;
        }

        let systemAlerts = [];
        let systemAlertsToDelete = [];

        let assignedTo = [{
            id: null,
            entity: {
                id: formData.id,
                label: "Unit",
                name: "Unit"
            }
        }];

        if (
            (formData?.driverBehaviorAlert?.rapidAcceleration ||
                formData?.driverBehaviorAlert?.hardBrake ||
                formData?.driverBehaviorAlert?.suddenStop ||
                formData?.driverBehaviorAlert?.hardTurn) && unit?.driverBehavior == 1 && unit?.unitVersion?.unitVersionDriverBehavior == 1
        ) {
            let config = {
                "driverBehaviorEvents": []
            }

            if (formData?.driverBehaviorAlert?.rapidAcceleration) config?.driverBehaviorEvents?.push("RapidAcceleration");
            if (formData?.driverBehaviorAlert?.hardBrake) config?.driverBehaviorEvents?.push("HardStop");
            if (formData?.driverBehaviorAlert?.suddenStop) config?.driverBehaviorEvents?.push("SuddenStop");
            if (formData?.driverBehaviorAlert?.hardTurn) config?.driverBehaviorEvents?.push("HardTurn");

            systemAlerts.push({
                id: unit?.driverBehaviorAlert?.id || null,
                assignedTo: assignedTo,
                config: JSON.stringify(config),
                name: `DriverBehavior - ${unit?.label}`,
                severity: "Medium",
                subscribers: alertContacts.map(contactId => {
                    return { alert: false, alertAfter: false, alertBefore: false, contactId: contactId, id: null }
                }),
                triggers: [],
                type: "DriverBehavior",
            });

        } else if (unit?.driverBehaviorAlert) {
            systemAlertsToDelete.push(unit?.driverBehaviorAlert);
        }

        if (formData?.towAlert?.tow && unit?.unitVersion?.tow == 1) {
            systemAlerts.push({
                id: unit?.towAlert?.id || null,
                assignedTo: assignedTo,
                config: null,
                name: `Tow - ${unit?.label}`,
                severity: "Medium",
                subscribers: alertContacts.map(contactId => {
                    return { alert: false, alertAfter: false, alertBefore: false, contactId: contactId, id: null }
                }),
                triggers: [],
                type: "Tow",
            });
        } else if (unit?.towAlert) {
            systemAlertsToDelete.push(unit?.towAlert);
        }

        if (formData?.motionAlert?.motion && unit?.unitVersion?.motion == 1) {
            systemAlerts.push({
                id: unit?.motionAlert?.id || null,
                assignedTo: assignedTo,
                config: JSON.stringify({}),
                name: `Motion - ${unit?.label}`,
                severity: "Medium",
                subscribers: alertContacts.map(contactId => {
                    return { alert: false, alertAfter: false, alertBefore: false, contactId: contactId, id: null }
                }),
                triggers: [],
                type: "Motion",
            });
        } else if (unit?.motionAlert) {
            systemAlertsToDelete.push(unit?.motionAlert);
        }

        if (formData?.consistentSpeedAlert?.consistentSpeed && unit?.unitVersion?.manufacturerId == 2) {
            systemAlerts.push({
                id: unit?.consistentSpeedAlert?.id || null,
                assignedTo: assignedTo,
                config: null,
                name: `Consistent Speed - ${unit?.label}`,
                severity: "Medium",
                subscribers: alertContacts.map(contactId => {
                    return { alert: false, alertAfter: false, alertBefore: false, contactId: contactId, id: null }
                }),
                triggers: [],
                type: "ConsistentSpeed",
            });
        } else if (unit?.consistentSpeedAlert) {
            systemAlertsToDelete.push(unit?.consistentSpeedAlert);
        }


        dispatch(fleetModalUpdateUnit({ unit: { ...objectTosave }, inputs: unitInputs, outputs: unitOutputs, systemAlerts: systemAlerts, systemAlertsToDelete: systemAlertsToDelete }))

    }, [formData, unit]);

    const filterIcon = (unit) => {
        let vehicleStatusId = unit?.vehicleStatusId || unit.lastReading?.vehicleStatusId;
        return vehicleStatus?.find(x => x.id == vehicleStatusId)
    }

    const onEcuChange = (checked, key) => {
        setFormData({
            ...formData,
            [key]: Number(checked),
        });
    };

    //Effects
    useEffect(() => {
        if (error) {
            let currentError = error?.error || messages["errorMessage"];
            if (currentError.includes("CompanyReadonly")) {
                currentError = messages["errorCompanyReadOnly"];
            } else if (currentError.includes("Label duplicated")) {
                currentError = messages['fleetModalUpdateUnit_duplicatedLabel'];
            } else if (currentError.includes("License Plate duplicated") || currentError.includes("assets.IX_assets_licensePlate_unique")) {
                currentError = messages['fleetModalUpdateUnit_duplicatedlicensePlace'];
            } else if (currentError.includes("VIN duplicated")) {
                currentError = messages['fleetModalUpdateUnit_duplicatedVin'];
            } else if (currentError.includes("The VIN is not valid")) {
                currentError = messages['fleetModalUpdateUnit_errorVin'];
            } else if (currentError.includes("ExternalId duplicated")) {
                currentError = messages['manage_createEditAsset_duplicatedexternalId']
            } else if (currentError.includes("InspectionTagId duplicated") || currentError.includes("assets.IX_assets_inspectionTagId_unique")) {
                currentError = messages['fleetModalUpdateUnit_duplicatedTagId']
            } else if (currentError.includes("ER_PARSE_ERROR")) {
                currentError = messages["errorMessage"];
            }

            NotificationManager.error(
                currentError,
                messages["error"],
                3000
            );
            dispatch(reset({ error: null }));
        }
    }, [error]);

    useEffect(() => {
        if (result) {
            if (result.result) {
                const assetId = unit?.asset?.id || formData.id;

                NotificationManager.success(
                    messages["fleetModalUpdateUnit_successMessage"],
                    messages["success"],
                    3000
                );
                dispatch(fleetModalGetUnit({ id: formData.id }));

                let driver = drivers?.find(d => d?.driverId == formData?.driverId);
                dispatch(catalogProcessItem("Unit", {
                    id: formData.id,
                    CUnits_id: formData.id,
                    label: formData.label,
                    Label: formData.label,
                    useEcuOdometer: formData.useEcuOdometer,
                    offsetEcuEngineOdometer:
                        formData.offsetEcuEngineOdometer === null || formData.offsetEcuEngineOdometer === undefined
                            ? null
                            : (user.isKph ? convertToMI(formData.offsetEcuEngineOdometer) : formData.offsetEcuEngineOdometer),
                    offsetEngineOdometer:
                        formData.offsetEngineOdometer === null || formData.offsetEngineOdometer === undefined
                            ? null
                            : (user.isKph ? convertToMI(formData.offsetEngineOdometer) : formData.offsetEngineOdometer),
                    useEcuEngineHours: formData.useEcuEngineHours,
                    offsetEcuEngineHours: formData.offsetEcuEngineHours,
                    offsetEngineHours: formData.offsetEngineHours,
                    useEcuVin: formData.useEcuVin,
                    tags: formData.tags,
                    driverId: formData?.driverId,
                    driverName: driver?.name || null,
                    ...(HasPermission(user, "defDrvr", [290]) ? { defaultDriverId: formData?.defaultDriverId } : {})
                }));

                let newUnits = units.filter(x => String(x.id) !== String(formData.id)).map(newUnit => {
                    let driverId = newUnit?.driverId;
                    let driverName = newUnit?.driverName;

                    if (newUnit?.id != formData.id && newUnit?.driverId == formData?.driverId) {
                        driverId = 0;
                        driverName = "";
                    }

                    return {
                        ...newUnit,
                        driverId,
                        driverName
                    };
                });

                dispatch(catalogProcessItems("Unit", newUnits));

                dispatch(catalogProcessItem("Asset", {
                    unit_id: formData.id,
                    id: assetId,
                    vin: formData.vin,
                    make: formData.make,
                    model: formData.model,
                    year: formData.year,
                    engine: formData.engine,
                    mpgHighway: formData.mpgHighway,
                    mpgCity: formData.mpgCity,
                    licensePlate: formData.licensePlate
                }))

                // --- Realtime slices sync (used by Fleet table) ---
                // 1) Realtime Unit (offsets, flags, driver, tags, status)
                const realtimeUnitPayload = {
                    id: formData.id,
                    label: formData.label,
                    useEcuOdometer: !!formData.useEcuOdometer,
                    offsetEngineOdometer:
                        formData.offsetEngineOdometer === null || formData.offsetEngineOdometer === undefined
                            ? null
                            : (user.isKph ? convertToMI(formData.offsetEngineOdometer) : formData.offsetEngineOdometer),
                    offsetEcuEngineOdometer:
                        formData.offsetEcuEngineOdometer === null || formData.offsetEcuEngineOdometer === undefined
                            ? null
                            : (user.isKph ? convertToMI(formData.offsetEcuEngineOdometer) : formData.offsetEcuEngineOdometer),
                    useEcuEngineHours: !!formData.useEcuEngineHours,
                    offsetEngineHours: formData.offsetEngineHours,
                    offsetEcuEngineHours: formData.offsetEcuEngineHours,
                    useEcuVin: !!formData.useEcuVin,
                    tags: formData.tags,
                    driverId: formData?.driverId,
                    defaultDriverId: HasPermission(user, "defDrvr", [290]) ? formData?.defaultDriverId : undefined,
                    vehicleStatusId: formData.vehicleStatusId,
                    asset: {
                        id: assetId,
                        vin: formData.vin,
                        ecuVin: formData.ecuVin,
                        make: formData.make,
                        model: formData.model,
                        year: formData.year,
                    },
                };

                dispatch(upsertRealtimeUnits([realtimeUnitPayload]));

                // 2) Realtime LastReading (current odometer / hours / vin / status)
                const realtimeLastReadingPayload = {
                    deviceId: formData.id,
                    id: formData.id,
                    engineOdometer: (user.isKph ? convertToMI(formData.engineOdometer) : formData.engineOdometer),
                    ecuEngineOdometer: (user.isKph ? convertToMI(formData.ecuEngineOdometer) : formData.ecuEngineOdometer),
                    engineHours: formData.engineHours,
                    ecuEngineHours: formData.ecuEngineHours,
                    vehicleStatusId: formData.vehicleStatusId,
                    vin: formData.vin,
                    ecuVin: formData.ecuVin,
                    ignoreValidation: true
                };

                dispatch(upsertRealtimeLastReadings([realtimeLastReadingPayload]));

                // 3) Realtime Asset (static asset data tied to unit)
                const realtimeAssetPayload = {
                    id: assetId,
                    unitId: formData.id,
                    vin: formData.vin,
                    ecuVin: formData.ecuVin,
                    make: formData.make,
                    model: formData.model,
                    year: formData.year,
                    engine: formData.engine,
                    mpgHighway: formData.mpgHighway,
                    mpgCity: formData.mpgCity,
                    licensePlate: formData.licensePlate,
                    registrationCountry: formData?.registrationCountry?.entityId ?? formData?.registrationCountry,
                    registrationState: formData?.registrationState?.entityId ?? formData?.registrationState,
                    registrationExpiration: formData?.registrationExpiration,
                    inspectionDeviceType: formData?.inspectionDeviceType?.entityId ?? formData?.inspectionDeviceType,
                    inspectionTagId: formData?.inspectionTagId,
                    powerUnitNumber: formData?.powerUnitNumber,
                    externalId: formData?.externalId,
                    isActive: typeof formData?.isActive === "object" ? !!formData?.isActive?.id : !!formData?.isActive,
                    chassisSerialNumber: formData?.chassisSerialNumber,
                    engineSerialNumber: formData?.engineSerialNumber,
                    maintenanceCompany: serializeMaintenanceCompany(formData?.maintenanceCompany),
                };
                dispatch(upsertRealtimeAssets([realtimeAssetPayload]));

                dispatch(catalogProcessItem("LastReading", {
                    deviceId: formData.id,
                    engineOdometer: (user.isKph ? convertToMI(formData.engineOdometer) : formData.engineOdometer),
                    ecuEngineOdometer: (user.isKph ? convertToMI(formData.ecuEngineOdometer) : formData.ecuEngineOdometer),
                    engineHours: formData.engineHours,
                    ecuVin: formData.ecuVin,
                    vehicleStatusId: formData.vehicleStatusId,
                    vin: formData.vin,
                }))

                dispatch(clearRedux("SETTINGS_DRIVER", { refresh: true }))
                setTimeout(() => handleClose(), 200);

            }
        }
    }, [result])

    useEffect(() => {
        if (open && unitId) {
            dispatch(catalogLoad(["drivers", "locations", "inputLabels", "timeZones", "unitGroupsList", "vehicleStatus"]))
            dispatch(fleetModalGetUnit({ id: unitId }));
            dispatch(fleetModalSearchEntityLists({}));
            dispatch(fleetModalSearchAssetSubtypes({}));
        } else {
            handleClose();
        }
    }, [open, unitId]);

    useEffect(() => {
        if (unit) {
            //Update icon
            setUpdateVehicleStatusIcon(filterIcon(unit) != null);

            //update units to evaluate
            // changeIdsCallback([unit])

            if (unit?.useEcuEngineHours && unit?.useEcuOdometer && unit?.useEcuSpeed && unit?.useEcuVin) {
                setEnableAll(true);
            } else {
                setEnableAll(false);
            }

            const countries = (entityLists || []).filter(it => it?.entityName === "COUNTRY");
            const states = (entityLists || []).filter(it => it?.entityName === "STATE");
            const noneCountry = { entityId: "NONE", label: messages['manage_createEditAsset_none'] };
            const noneState = { entityId: "NONE", label: messages['manage_createEditAsset_none'], parentId: "NONE" };

            const rawCountryId = unit?.asset?.registrationCountry;
            let rawStateId = unit?.asset?.registrationState;
            rawStateId = rawStateId === "CO-" ? "CO-CO" : rawStateId === "PE-" ? "PE-PE" : rawStateId;

            const registrationCountryObj =
                rawCountryId ? (countries.find(c => c.entityId === rawCountryId) || noneCountry) : noneCountry;
            const registrationStateObj =
                rawStateId ? (states.find(s => s.entityId === rawStateId) || noneState) : noneState;

            const inspectionDeviceTypeObj = unit?.asset?.inspectionDeviceType
                ? (entityLists || []).find(it => it?.entityName === "InspectionDeviceType" && it?.entityId === unit?.asset?.inspectionDeviceType)
                : null;

            const assetTypeObj = unit?.asset?.assetTypeId
                ? (unit?.asset?.assetType || (entityLists || []).find(it => it?.entityName === "AssetType" && it?.entityId === unit?.asset?.assetTypeId))
                : null;

            let alertContacts = [];
            if (unit?.alertContacts?.length) {
                alertContacts = [...unit?.alertContacts];
            }

            if (unit?.driverBehavior?.subscribers?.length) {
                unit?.driverBehavior?.subscribers?.forEach(subscriber => {
                    if (!alertContacts?.find(c => c.id == subscriber?.contact?.id)) {
                        alertContacts.push(subscriber?.contact);
                    }
                });
            }

            if (unit?.towAlert?.subscribers?.length) {
                unit?.towAlert?.subscribers?.forEach(subscriber => {
                    if (!alertContacts?.find(c => c.id == subscriber?.contact?.id)) {
                        alertContacts.push(subscriber?.contact);
                    }
                });
            }

            if (unit?.motionAlert?.subscribers?.length) {
                unit?.motionAlert?.subscribers?.forEach(subscriber => {
                    if (!alertContacts?.find(c => c.id == subscriber?.contact?.id)) {
                        alertContacts.push(subscriber?.contact);
                    }
                });
            }

            if (unit?.consistentSpeedAlert?.subscribers?.length) {
                unit?.consistentSpeedAlert?.subscribers?.forEach(subscriber => {
                    if (!alertContacts?.find(c => c.id == subscriber?.contact?.id)) {
                        alertContacts.push(subscriber?.contact);
                    }
                });
            }

            if (alertContacts?.length && alertContacts?.find(c => c?.id === -1)) {
                alertContacts = [...contacts];
            }

            const momentFormat = user?.dateformat ? `${GetFormatForMoment(user.dateformat)} h:mm a` : null;

            const parseMaintenanceCompany = (raw) => {
                if (typeof raw !== "string" || raw.trim() === "") return null;

                const result = {};
                const regex = /([a-zA-Z0-9_]+)\s*=\s*([^,}]+)/g;
                let match;
                while ((match = regex.exec(raw)) !== null) {
                    result[match[1]] = match[2].trim();
                }

                return result.id ? result : null;
            };

            let initialValues = {
                ...formData,
                id: unit?.id,
                type: unit?.type,
                hid: unit?.hid,
                label: unit?.label || "",
                useEcuOdometer: unit?.useEcuOdometer || false,
                engineOdometer: unit?.lastReading?.engineOdometer && Number(unit?.lastReading?.engineOdometer).toFixed(2) || 0,
                ecuEngineOdometer: unit?.lastReading?.ecuEngineOdometer && Number(unit?.lastReading?.ecuEngineOdometer).toFixed(2) || 0,
                offsetEcuEngineOdometer:
                    unit?.offsetEcuEngineOdometer === null || unit?.offsetEcuEngineOdometer === undefined
                        ? null
                        : Number(unit?.offsetEcuEngineOdometer).toFixed(2),
                offsetEngineOdometer:
                    unit?.offsetEngineOdometer === null || unit?.offsetEngineOdometer === undefined
                        ? null
                        : Number(unit?.offsetEngineOdometer).toFixed(2),
                useEcuEngineHours: unit?.useEcuEngineHours || false,
                engineHours: unit?.lastReading?.engineHours && Number(unit?.lastReading?.engineHours).toFixed(2) || 0,
                ecuEngineHours: unit?.lastReading?.ecuEngineHours && Number(unit?.lastReading?.ecuEngineHours).toFixed(2) || 0,
                offsetEcuEngineHours:
                    unit?.offsetEcuEngineHours === null || unit?.offsetEcuEngineHours === undefined
                        ? null
                        : Number(unit?.offsetEcuEngineHours).toFixed(2),
                offsetEngineHours:
                    unit?.offsetEngineHours === null || unit?.offsetEngineHours === undefined
                        ? null
                        : Number(unit?.offsetEngineHours).toFixed(2),
                useEcuSpeed: unit?.useEcuSpeed || false,
                speed: unit?.lastReading?.speed && Number(unit?.lastReading?.speed).toFixed(2) || 0,
                ecuSpeed: unit?.lastReading?.ecuSpeed && Number(unit?.lastReading?.ecuSpeed).toFixed(2) || 0,
                useEcuVin: unit?.useEcuVin || false,
                ecuVin: unit?.lastReading?.ecuVin || "",
                unitGroups: unit?.unitGroups || [],
                driverId: unit?.driverId,
                driver: unit?.driver?.id ? {
                    driverId: unit?.driver?.id || null,
                    name: unit?.driver?.title || "",
                } : null,
                defaultDriverId: HasPermission(user, "defDrvr", [290]) ? unit?.defaultDriverId : null,
                defaultDriver: unit?.defaultDriver?.id && HasPermission(user, "defDrvr", [290]) ? { driverId: unit?.defaultDriver?.id, name: unit?.defaultDriver?.title } : null,
                locationId: unit?.locationId,
                location: unit?.location,
                timeZoneId: unit?.timeZone?.id,
                timeZone: unit?.timeZone,
                offsetEngineHoursUpdatedOnLabel: getFormattedDateByTimeZone(unit?.offsetEngineHoursUpdatedOn, user?.timeZone, messages, momentFormat, false, locale),
                offsetEngineOdometerUpdatedOnLabel: getFormattedDateByTimeZone(unit?.offsetEngineOdometerUpdatedOn, user?.timeZone, messages, momentFormat, false, locale),
                supportedOutputs: Number(unit?.unitVersion?.supportedOutputs || 0) || 0,
                supportedInputs: Number(unit?.unitVersion?.supportedInputs || 0) || 0,
                inputs: unit?.inputs || [],
                outputs: buildPositionalOutputs(unit),
                notes: unit?.notes || "",
                jbus: unit?.jbus,
                vbus: unit?.vbus,
                vehicleStatusId: unit?.vehicleStatusId,
                tags: unit?.tags || [],

                //Alert
                lowBattery: unit?.calampSettings?.lowBattery ? true : false,
                noGps: unit?.calampSettings?.noGps ? true : false,
                noGpsTimes: unit?.calampSettings?.noGpsTimes || 0,
                noGpsDays: unit?.calampSettings?.noGpsDays || 0,

                consistentSpeed: unit?.calampSettings?.consistentSpeed || 0,
                alertContacts: alertContacts,
                //Driver Behavior
                driverBehaviorAlert: {},
                //Tow
                towAlert: {
                    tow: unit?.towAlert ? true : false
                },
                //Motion
                motionAlert: {
                    motion: unit?.motionAlert ? true : false
                },
                //Consistent Speed
                consistentSpeedAlert: {
                    consistentSpeed: unit?.consistentSpeedAlert ? true : false
                },

                /* New */
                vin: unit?.asset?.vin,
                year: unit?.asset?.year,
                make: unit?.asset?.make,
                model: unit?.asset?.model,
                engine: unit?.asset?.engine,
                licensePlate: unit?.asset?.licensePlate,
                mpgHighway: unit?.asset?.mpgHighway || 0,
                mpgCity: unit?.asset?.mpgCity && Number(unit?.asset?.mpgCity) || 0,

                /* Asset */
                assetSubtypeId: unit?.asset?.assetSubtypeId || null,
                assetSubtype: unit?.asset?.assetSubtypeId ? unit?.asset?.assetSubtype : null,
                assetTypeId: unit?.asset?.assetTypeId || null,
                assetType: assetTypeObj,

                registrationCountry: registrationCountryObj,
                registrationState: registrationStateObj,
                registrationExpiration: unit?.asset?.registrationExpiration,
                inspectionDeviceType: inspectionDeviceTypeObj,
                inspectionTagId: unit?.asset?.inspectionTagId || null,
                powerUnitNumber: HasPermission(user, "zlogs") ? unit?.asset?.powerUnitNumber : null,
                externalId: unit?.asset?.externalId,
                isActive: unit?.asset?.isActive,
                maintenanceCompany: parseMaintenanceCompany(unit?.asset?.maintenanceCompany),
                chassisSerialNumber: unit?.asset?.chassisSerialNumber || "",
                engineSerialNumber: unit?.asset?.engineSerialNumber || ""
            }

            if (unit?.driverBehaviorAlert && unit?.driverBehaviorAlert?.configJSON) {
                let config = JSON.parse(unit?.driverBehaviorAlert?.configJSON);
                if (config) {
                    initialValues.driverBehaviorAlert = {
                        rapidAcceleration: config?.driverBehaviorEvents?.includes("RapidAcceleration"),
                        hardBrake: config?.driverBehaviorEvents?.includes("HardStop"),
                        suddenStop: config?.driverBehaviorEvents?.includes("SuddenStop"),
                        hardTurn: config?.driverBehaviorEvents?.includes("HardTurn"),
                    };
                }
            }

            if (unit?.unitVersion?.manufacturerId === 2) {
                initialValues = {
                    ...initialValues,
                    manufacturerId: unit?.unitVersion?.manufacturerId,
                    consistentSpeed: unit?.calampSettings?.consistentSpeed,
                };
            }

            if (initialValues.year === 0) {
                initialValues.year = null;
            }

            if (!initialValues.jbus && !initialValues.vbus) {
                initialValues.useEcuOdometer = false;
                initialValues.useEcuEngineHours = false;
                initialValues.useEcuSpeed = false;
                initialValues.useEcuVin = false;
            }

            let dashOdometer = null;
            if (initialValues.useEcuOdometer) {
                const hasBase = initialValues.ecuEngineOdometer !== null && initialValues.ecuEngineOdometer !== undefined;
                const hasOffset = initialValues.offsetEcuEngineOdometer !== null && initialValues.offsetEcuEngineOdometer !== undefined;
                if (hasBase && hasOffset) {
                    dashOdometer = Number(
                        getValueFloat(initialValues.offsetEcuEngineOdometer) +
                        getValueFloat(initialValues.ecuEngineOdometer)
                    );
                }
            } else {
                const hasBase = initialValues.engineOdometer !== null && initialValues.engineOdometer !== undefined;
                const hasOffset = initialValues.offsetEngineOdometer !== null && initialValues.offsetEngineOdometer !== undefined;
                if (hasBase && hasOffset) {
                    dashOdometer = Number(
                        getValueFloat(initialValues.offsetEngineOdometer) +
                        getValueFloat(initialValues.engineOdometer)
                    );
                }
            }

            if (dashOdometer !== null && Number.isFinite(dashOdometer)) {
                if (dashOdometer > 100000000) {
                    dashOdometer = 100000000;
                }
                if (user.isKph) {
                    dashOdometer = convertToKPHWithDecimals(dashOdometer, 2);
                } else {
                    dashOdometer = formatMiles(dashOdometer);
                }
                initialValues.dashOdometer = dashOdometer;
            } else {
                initialValues.dashOdometer = null;
            }

            if (user.isKph) {
                if (initialValues.ecuEngineOdometer !== null && initialValues.ecuEngineOdometer !== undefined) {
                    initialValues.ecuEngineOdometer = convertToKPHWithDecimals(initialValues.ecuEngineOdometer, 2);
                }
                if (initialValues.offsetEcuEngineOdometer !== null && initialValues.offsetEcuEngineOdometer !== undefined) {
                    initialValues.offsetEcuEngineOdometer = convertToKPHWithDecimals(initialValues.offsetEcuEngineOdometer, 2);
                }
                if (initialValues.engineOdometer !== null && initialValues.engineOdometer !== undefined) {
                    initialValues.engineOdometer = convertToKPHWithDecimals(initialValues.engineOdometer, 2);
                }
                if (initialValues.offsetEngineOdometer !== null && initialValues.offsetEngineOdometer !== undefined) {
                    initialValues.offsetEngineOdometer = convertToKPHWithDecimals(initialValues.offsetEngineOdometer, 2);
                }
            } else {
                if (initialValues.ecuEngineOdometer !== null && initialValues.ecuEngineOdometer !== undefined) {
                    initialValues.ecuEngineOdometer = formatMiles(initialValues.ecuEngineOdometer);
                }
                if (initialValues.offsetEcuEngineOdometer !== null && initialValues.offsetEcuEngineOdometer !== undefined) {
                    initialValues.offsetEcuEngineOdometer = formatMiles(initialValues.offsetEcuEngineOdometer);
                }
                if (initialValues.engineOdometer !== null && initialValues.engineOdometer !== undefined) {
                    initialValues.engineOdometer = formatMiles(initialValues.engineOdometer);
                }
                if (initialValues.offsetEngineOdometer !== null && initialValues.offsetEngineOdometer !== undefined) {
                    initialValues.offsetEngineOdometer = formatMiles(initialValues.offsetEngineOdometer);
                }
            }

            let dashEngineHours = null;
            if (initialValues.useEcuEngineHours) {
                const hasBase = initialValues.ecuEngineHours !== null && initialValues.ecuEngineHours !== undefined;
                const hasOffset = initialValues.offsetEcuEngineHours !== null && initialValues.offsetEcuEngineHours !== undefined;
                if (hasBase && hasOffset) {
                    dashEngineHours =
                        Number(
                            getValueFloat(initialValues.offsetEcuEngineHours) +
                            getValueFloat(initialValues.ecuEngineHours)
                        );
                }
            } else {
                const hasBase = initialValues.engineHours !== null && initialValues.engineHours !== undefined;
                const hasOffset = initialValues.offsetEngineHours !== null && initialValues.offsetEngineHours !== undefined;
                if (hasBase && hasOffset) {
                    dashEngineHours =
                        Number(
                            getValueFloat(initialValues.offsetEngineHours) +
                            getValueFloat(initialValues.engineHours)
                        );
                }
            }

            if (dashEngineHours !== null && Number.isFinite(dashEngineHours)) {
                if (dashEngineHours > 1000000) {
                    dashEngineHours = 1000000;
                }
                initialValues.dashEngineHours = dashEngineHours;
            } else {
                initialValues.dashEngineHours = null;
            }

            let sInputs = [];

            let sOutputs = [];

            if (Number(unit?.unitVersion?.supportedInputs || 0) > 0) {
                for (let i = 1; i <= Number(unit?.unitVersion?.supportedInputs || 0); i++) {
                    sInputs.push(i);
                }
            }


            if (Number(unit?.unitVersion?.supportedOutputs || 0) > 0) {
                for (let i = 0; i < Number(unit?.unitVersion?.supportedOutputs || 0); i++) {
                    sOutputs.push(i + 1);
                }
            }

            if (unit?.inputs?.length) {
                unit?.inputs.forEach((input, index) => {
                    let currentInput = inputLabels.find(label => label.id === input.labelId);
                    initialValues[`input${input?.position}`] = currentInput;
                });
            }

            if (unit?.outputs?.length) {

                const selectOutputOptions = [
                    { type: "Toggle", label: messages["unit_toggle_options"] },
                    { type: "Pulse", label: messages["unit_pulse_options"] },
                    { type: "singlePulse", label: messages["unit_pulse_single_option"] }
                ];

                unit?.outputs.forEach((input, index) => {
                    let currentOutput = selectOutputOptions.find(label => label.type === input.type);
                    initialValues[`output${input?.position}`] = input?.name;
                    initialValues[`outputSelector${input?.position}`] = currentOutput;
                });
            }

            setFormData(initialValues);

            setSupportedInputs(Number(unit?.unitVersion?.supportedInputs || 0));
            setSupportedOutputs(Number(unit?.unitVersion?.supportedOutputs || 0));

            setInputsCounter(sInputs);
            setOutputsCounter(sOutputs);

            //setTimeout(() => {
            unitFormRef.setValues(initialValues)
            //}, 100)
        }
    }, [unit, inputLabels]);

    const tabs = [
        {
            label: messages["fleetModalUpdateUnit_unit"],
            icon: "directions_car",
            component: <Unit
                formData={formData}
                setFormData={setFormData}
                udpateVehicleStatusIcon={udpateVehicleStatusIcon}
                setUpdateVehicleStatusIcon={setUpdateVehicleStatusIcon}
                onEcuChange={onEcuChange}
                enableAll={enableAll}
                setEnableAll={setEnableAll}
                unitFormRef={unitFormRef}
            />,
            visible: true
        },
        {
            label: messages["fleetModalUpdateUnit_asset"],
            icon: "settings",
            component: <Asset
                formData={formData}
                setFormData={setFormData}
                onEcuChange={onEcuChange}
                unitFormRef={unitFormRef}
                changes={changes}
                setChanges={setChanges}
                formWithErrors={formWithErrors}
            />,
            visible: true
        },
        {
            label: messages["fleetModalUpdateUnit_inputOutput"],
            icon: "settings_input_composite",
            component: <InputOutput
                outputsCounter={outputsCounter}
                setOutputsCounter={setOutputsCounter}
                inputsCounter={inputsCounter}
                setInputsCounter={setInputsCounter}
                formData={formData}
                setFormData={setFormData}
                changes={changes}
                setChanges={setChanges}
                unitFormRef={unitFormRef}
                setTab={setTab}
            />,
            visible: (supportedOutputs > 0 || supportedInputs > 0)
        },
        {
            label: messages["fleetModalUpdateUnit_notes"],
            icon: "note_alt",
            component: <Notes
                formData={formData}
                setFormData={setFormData}
            />,
            visible: true
        },
        {
            label: messages["fleetModalUpdateUnit_tags"],
            icon: "sell",
            component: <Tags
                formData={formData}
                setFormData={setFormData}
            />,
            visible: true
        }
    ];

    const visibleTabs = tabs.filter(tab => tab.visible);
    const maxTabIndex = visibleTabs.length - 1;
    const safeTab = typeof tab === "number" && tab >= 0 && tab <= maxTabIndex ? tab : 0;

    useEffect(() => {
        if (deviceVin?.error) {
            NotificationManager.error(
                messages["manage_createEditAsset_noInformationFoundVIN"],
                messages["error"],
                3000
            );
            dispatch(reset({ deviceVin: null }));
        } else if (deviceVin) {
            if (deviceVin?.invalidVin) {
                NotificationManager.warning(
                    messages[deviceVin.checkDigitError ? "fleetModalUpdateUnit_warningVinCheckDigit" : "fleetModalUpdateUnit_errorVin"],
                    messages["warning"],
                    5000
                );
            }
            setFormData({
                ...formData,
                vin: deviceVin?.VIN,
                year: deviceVin?.ModelYear,
                make: deviceVin?.Make,
                model: deviceVin?.Model,
                engine: deviceVin?.DisplacementL
            })
            unitFormRef.setValues({
                vin: deviceVin?.VIN,
                year: deviceVin?.ModelYear,
                make: deviceVin?.Make,
                model: deviceVin?.Model,
                engine: deviceVin?.DisplacementL
            })
            setChanges({ ...changes, engine: true, model: true, make: true, year: true });
            dispatch(reset({ deviceVin: null }));
        }
    }, [deviceVin])

    useEffect(() => {
        if (titleRef.current) {
            setIsEllipsis(titleRef.current.scrollWidth > titleRef.current.clientWidth);
        }
    }, [unit?.label, messages]);

    const getModalTitle = (unit, messages) => {
        const label = unit?.label?.toString() || '';
        const textEllipsisStyles = {
            fontWeight: 'bold',
            fontSize: '1rem',
            lineHeight: 1.5,
            letterSpacing: '.00938em',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: 377,
            display: 'block'
        };

        const content = (
            <div ref={titleRef} style={textEllipsisStyles}>
                {messages['fleetModalUpdateUnit_unit']} {label}
            </div>
        );

        if (isEllipsis) {
            return (
                <Tooltip title={`${messages['fleetModalUpdateUnit_unit']} ${label}`}>
                    {content}
                </Tooltip>
            );
        }
        return content;
    };

    return (
        <Modal
            title={
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    backgroundColor: 'white',
                    height: 30,
                    padding: '8px 8px 8px 16px',
                    border: 'solid 1px #e4e4e4',
                }}
                >
                    {getModalTitle(unit, messages)}
                    <IconButton
                        onClick={handleClose}
                        aria-label={messages["close"]}
                        title={messages["close"]}
                        tooltip={messages["close"]}
                        icon="close"
                    />

                </div>
            }
            size="small"
            open={open}
            handleClose={handleClose}
            loading={loading || loadingDecodeVin || loadingAssetSubtypes || loadingAssetTypeConfig}
            footer={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <div>
                        <Button
                            variant="secondary"
                            style={{ marginRight: "5px" }}
                            onClick={() => handleClose()}
                            isDisabled={loading}
                        >
                            {messages["cancel"]}
                        </Button>
                        {
                            HasPermission(user, "", [13]) ?
                                <Button
                                    variant="primary"
                                    onClick={() => unitFormRef?.submit()}
                                    isDisabled={loading}
                                >
                                    {messages["save"]}
                                </Button> : <></>
                        }

                    </div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        {
                            HasPermission(user, "trbusr", [262]) &&
                            <div style={{ display: 'flex', marginRight: 10 }}>
                                <Icon>troubleshoot</Icon>
                                <a
                                    onClick={() => {
                                        dispatch(openTroubleshootDeviceModal({ open: true, unit: unit }));
                                        handleClose();
                                    }} style={{
                                        textDecoration: 'underline',
                                        cursor: 'pointer',
                                        fontSize: locale === 'es' && '12px'
                                    }}>{messages['unit_settings_start_troubleshooting']}</a>
                            </div>
                        }
                    </div>
                </div>
            }
        >
            <div style={{ padding: "0 25px" }}>
                <Form
                    ref={unitFormRef?.ref}
                    onSubmit={() => {
                        const values = unitFormRef?.ref?.current?.getValues();
                        const label = values.label?.trim();

                        if (label === "") {
                            NotificationManager.error(
                                messages["fleetModalUpdateUnit_labelEmpty"],
                                messages["error"],
                                3000
                            );
                            setTab(0);
                            return;
                        }

                        const isZonarDevice = unit?.deviceModel?.manufacturer?.name === "Zonar";
                        const hasEvirPermission = HasPermission(user, "evir");
                        const hasZlogsPermission = HasPermission(user, "zlogs");

                        const requiredGeneralFieldsMissing = !formData?.timeZone || !formData?.label;

                        const evirFieldsMissing = hasEvirPermission && (!formData?.assetSubtype || !formData?.assetType);

                        // Power Unit Number validation: required for zlogs users when asset type is not TRAILER/DOLLY
                        const assetTypeLabel = formData?.assetType?.label?.toUpperCase();
                        const isTrailerOrDolly = ['TRAILER', 'DOLLY'].includes(assetTypeLabel);
                        const zonarPowerUnitMissing = hasZlogsPermission && !isTrailerOrDolly && (!formData?.powerUnitNumber || formData?.powerUnitNumber?.trim?.() === '');

                        const licenseInfoMissing = (HasPermission(user, 'zlogs') && values?.registrationCountry?.entityId != "NONE" && values?.registrationState?.entityId === "NONE")

                        if (licenseInfoMissing) {
                            NotificationManager.warning(
                                messages['manage_createEditAsset_registrationRequired'],
                                messages["error"],
                                3000
                            );

                        }

                        const mpgCityInvalid = changes?.mpgCity && formData?.mpgCity > 200;

                        const mpgHighwayInvalid = changes?.mpgHighway && formData?.mpgHighway > 200;

                        const engineSizeInvalid = changes?.engine && parseFloat(formData?.engine) > 10000;

                        const nextYear = new Date().getFullYear() + 1;
                        const yearInvalid = changes?.year && formData?.year && (formData?.year > nextYear)

                        const hasGeneralValidationErrors =
                            (requiredGeneralFieldsMissing || evirFieldsMissing || zonarPowerUnitMissing ||
                                licenseInfoMissing || mpgCityInvalid || mpgHighwayInvalid || engineSizeInvalid || yearInvalid);
                        if (hasGeneralValidationErrors) {

                            // Set relevant tab
                            if (!formData?.label || !formData?.timeZone) setTab(0);
                            else if (licenseInfoMissing || mpgCityInvalid || mpgHighwayInvalid || engineSizeInvalid || yearInvalid || evirFieldsMissing || zonarPowerUnitMissing) {
                                if (evirFieldsMissing) {
                                    setFormWithErrors(true)
                                }
                                setTab(1);
                            }

                            return;
                        }

                        if (hasInvalidUnitOutputs({ outputsCounter, formData, values })) {
                            setTab(2);
                            NotificationManager.info(
                                messages["fleetModalUpdateUnit_requiredFields"],
                                messages["fleetModalUpdateUnit_alert_title"],
                                5000
                            );
                            return;
                        }

                        // All validations passed
                        handleUpdateSubmit();
                    }}
                >
                    {
                        unit &&
                        <Tabs
                            tab={safeTab}
                            type="icon-indicator"
                            border
                            onChange={(value) => setTab(value)}
                        >
                            {visibleTabs.map((item, index) => {
                                const { label, component, icon } = item;
                                return (
                                    <TabContainer key={index} label={label} index={index} iconIndicator={icon} style={{ padding: '24px 0' }}>
                                        {component}
                                    </TabContainer>
                                );
                            })}
                        </Tabs>
                    }
                </Form>
            </div>
            <NotificationContainer />
        </Modal>
    );
};

export default ModalUpdateUnit;
